import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { getExternalSlabStyle } from '../../assets/externalSlabCatalogue';
import {
  EMPTY_HARDWARE_GUIDES,
  hardwarePlacementAvailability,
  type HardwarePlacementAvailability,
} from '../../assets/hardwareGuides';
import { withExclusiveAccessory } from '../../hardware/hardwareCompatibility';
import {
  DEFAULT_HARDWARE_COLOUR_FAMILY_ID,
  HARDWARE_COLOUR_FAMILIES,
  HARDWARE_PRODUCTS,
  HARDWARE_RANGES,
  getHardwareProduct,
  getHardwareRange,
  hardwareProductsByFamilyAndColour,
  hardwareProductsByRange,
  hardwareProductsByRangeAndColour,
  type HardwareColourFamilyId,
  type HardwareProduct,
  type HardwareRange,
  type HardwareRangeId,
} from '../../hardware/hardwareCatalog';
import { escutcheonPlacementIds, slabPlacementIdForProduct } from '../../hardware/placeHardware';
import {
  selectedLockingMechanism,
  selectedLockingMechanismId,
  selectedOperatingHandleId,
  selectedLetterplate,
  furniturePairingHint,
  hardwareProductPairingReason,
  nextEscutcheonSelection,
  nextLetterplateSelection,
  withColourFamily,
  withLockingMechanism,
  withOperatingHandle,
  withLetterplate,
} from '../../hardware/selectHardware';
import type { DoorConfiguration } from '../../types/doorConfiguration';
import { ChoiceCard } from '../ChoiceCard';
import { PlaceholderCatalogCard } from '../PlaceholderCatalogCard';
import { useExternalSlabArtwork } from '../../surfaces/external/useExternalSlabArtwork';
import { Button } from '../../ui/button';
import { cn } from '../../ui/cn';

function furnitureProductTitle(product: HardwareProduct): string {
  if (product.family === 'pull-bar') {
    const kind = product.pullBarProfile === 'flat' ? 'Flat bar handle' : 'Bar handle';
    return product.pullBarLengthMm ? `${kind} ${product.pullBarLengthMm}mm` : kind;
  }
  if (product.family === 'lever') return 'Lever handle';
  if (product.role === 'escutcheon') return product.name;
  if (product.family === 'letterplate') return product.name;
  return product.name;
}

function isOperatingHandle(product: HardwareProduct): boolean {
  return product.family === 'lever' || product.family === 'pull-bar';
}

function productOfferedOnSlab(
  product: HardwareProduct,
  availability: HardwarePlacementAvailability
): boolean {
  switch (product.family) {
    case 'lever':
      return product.position === 'heritage' ? availability.heritage : availability.standardHandle;
    case 'pull-bar':
      return availability.pullBar;
    case 'knocker':
      return availability.knocker;
    case 'spyhole':
      return availability.spyhole;
    case 'letterplate':
      return availability.letterplatePositions.length > 0;
    case 'locking':
      if (product.role === 'escutcheon') {
        return availability.standardHandle || availability.heritage;
      }
      if (product.role === 'finger-pull-escutcheon') {
        return product.position === 'heritage' ? availability.heritage : availability.standardHandle;
      }
      return true;
    default:
      return true;
  }
}

function rangeLetterplatesForColour(colourFamilyId: HardwareColourFamilyId): HardwareProduct[] {
  return HARDWARE_RANGES.flatMap((range) =>
    hardwareProductsByRangeAndColour(range.id, colourFamilyId).filter(
      (product) => product.family === 'letterplate'
    )
  );
}

function colourTaggedProductsFromOtherRanges(
  rangeId: HardwareRangeId,
  colourFamilyId: HardwareColourFamilyId
): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter(
    (product) =>
      Boolean(product.rangeId) &&
      product.rangeId !== rangeId &&
      product.colourFamilyId === colourFamilyId
  );
}

function rangeOwnsFamily(
  rangeId: HardwareRangeId,
  isFamily: (product: HardwareProduct) => boolean
): boolean {
  return hardwareProductsByRange(rangeId).some(isFamily);
}

function lockingProductsForFurniture(
  colourFamilyId: HardwareColourFamilyId,
  browsingRangeId: HardwareRangeId | null
): HardwareProduct[] {
  const taggedThisColour = HARDWARE_PRODUCTS.filter(
    (product) => product.family === 'locking' && product.colourFamilyId === colourFamilyId
  );
  const taggedRoles = new Set(taggedThisColour.map((product) => product.role));
  const untagged = hardwareProductsByFamilyAndColour('locking', colourFamilyId).filter(
    (product) => !product.rangeId && !taggedRoles.has(product.role)
  );
  if (!browsingRangeId) return untagged;
  const fromRange = taggedThisColour.filter((product) => product.rangeId === browsingRangeId);
  return [...fromRange, ...untagged];
}

function ExtraGlyph({ kind }: { kind: 'letterplate' | 'knocker' | 'spyhole' }) {
  if (kind === 'letterplate') {
    return (
      <svg viewBox="0 0 80 60" className="h-full w-full" aria-hidden>
        <rect x="16" y="22" width="48" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" className="text-foreground/70" />
        <path d="M16 22 L40 34 L64 22" fill="none" stroke="currentColor" strokeWidth="2" className="text-foreground/70" />
      </svg>
    );
  }
  if (kind === 'spyhole') {
    return (
      <svg viewBox="0 0 80 60" className="h-full w-full" aria-hidden>
        <circle cx="40" cy="30" r="12" fill="none" stroke="currentColor" strokeWidth="2" className="text-foreground/70" />
        <circle cx="40" cy="30" r="4" fill="currentColor" className="text-foreground/70" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 80 60" className="h-full w-full" aria-hidden>
      <circle cx="40" cy="30" r="10" fill="none" stroke="currentColor" strokeWidth="2" className="text-foreground/70" />
      <rect x="38" y="18" width="4" height="10" rx="1" fill="currentColor" className="text-foreground/70" />
    </svg>
  );
}

function hasAccessory(configuration: DoorConfiguration, id: 'knocker' | 'spyhole'): boolean {
  return configuration.hardware.external.some((item) => item.id === id);
}

function RangeThumb({ products }: { products: HardwareProduct[] }) {
  const thumbs = products.slice(0, 2);
  return (
    <div
      className={cn(
        'aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted',
        thumbs.length > 1 && 'grid grid-cols-2'
      )}
    >
      {thumbs.map((product) => (
        <img
          key={product.id}
          src={product.publicPath}
          alt=""
          className="h-full w-full object-contain p-2"
        />
      ))}
    </div>
  );
}

function RangeCard({
  range,
  products,
  selected,
  onSelect,
}: {
  range: HardwareRange;
  products: HardwareProduct[];
  selected: boolean;
  onSelect: () => void;
}) {
  const count = products.length;
  return (
    <button
      type="button"
      aria-label={range.label}
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'door-designer-choice relative flex w-full flex-col rounded-xl border border-solid bg-card p-4 text-left shadow-xs transition-colors',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-muted/50'
      )}
    >
      {selected ? (
        <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" />
        </span>
      ) : null}
      <RangeThumb products={products} />
      <span className="mt-3 flex min-w-0 flex-col">
        <span className="text-sm font-semibold">{range.label}</span>
        <span className="mt-1 text-sm text-muted-foreground">
          {count === 0 ? 'Coming soon' : count === 1 ? '1 option' : `${count} options`}
        </span>
      </span>
    </button>
  );
}

function ProductImageCard({
  product,
  selected,
  offered,
  unavailableReason,
  caption,
  onSelect,
}: {
  product: HardwareProduct;
  selected: boolean;
  offered: boolean;
  unavailableReason?: string;
  caption?: string;
  onSelect: () => void;
}) {
  return (
    <ChoiceCard
      title={furnitureProductTitle(product)}
      description={offered ? caption : unavailableReason}
      selected={selected}
      disabled={!offered}
      tickPlacement="top-right"
      onSelect={onSelect}
    >
      <div className="aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted">
        <img src={product.publicPath} alt="" className="h-full w-full object-contain p-2" />
      </div>
    </ChoiceCard>
  );
}

function productCardAvailability(
  product: HardwareProduct,
  availability: HardwarePlacementAvailability,
  configuration: DoorConfiguration
): { offered: boolean; unavailableReason?: string } {
  if (!productOfferedOnSlab(product, availability)) {
    return { offered: false, unavailableReason: 'Not declared on this style' };
  }
  const pairingReason = hardwareProductPairingReason(product, configuration.hardware);
  if (pairingReason) {
    return { offered: false, unavailableReason: pairingReason };
  }
  return { offered: true };
}

function LockingMechanismsSection({
  configuration,
  availability,
  products,
  footer,
  onSelect,
}: {
  configuration: DoorConfiguration;
  availability: HardwarePlacementAvailability;
  products: HardwareProduct[];
  footer?: React.ReactNode;
  onSelect: (product: HardwareProduct) => void;
}) {
  const selectedId = selectedLockingMechanismId(configuration.hardware);
  const pairingHint = furniturePairingHint(configuration.hardware);
  return (
    <section>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Locking mechanisms
      </p>
      {products.length === 0 ? (
        <p className="text-sm text-muted-foreground">No locking in this colour yet.</p>
      ) : (
        <div role="radiogroup" aria-label="Locking mechanisms" className="grid grid-cols-2 gap-3">
          {products.map((product) => {
            const card = productCardAvailability(product, availability, configuration);
            return (
              <ProductImageCard
                key={product.id}
                product={product}
                selected={selectedId === product.id}
                offered={card.offered}
                unavailableReason={card.unavailableReason}
                onSelect={() => onSelect(product)}
              />
            );
          })}
        </div>
      )}
      {footer}
      {pairingHint ? (
        <p className="mt-3 text-sm text-muted-foreground">{pairingHint}</p>
      ) : null}
    </section>
  );
}

function CatalogProductSection({
  title,
  ariaLabel,
  products,
  emptyMessage,
  selectedId,
  isSelected,
  configuration,
  availability,
  captionForProduct,
  footer,
  onSelect,
}: {
  title: string;
  ariaLabel: string;
  products: HardwareProduct[];
  emptyMessage?: string;
  selectedId?: string;
  isSelected?: (product: HardwareProduct) => boolean;
  configuration: DoorConfiguration;
  availability: HardwarePlacementAvailability;
  captionForProduct?: (product: HardwareProduct) => string | undefined;
  footer?: React.ReactNode;
  onSelect: (product: HardwareProduct) => void;
}) {
  if (products.length === 0 && !emptyMessage) return null;
  const productSelected = (product: HardwareProduct) =>
    isSelected ? isSelected(product) : selectedId === product.id;
  return (
    <section>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {products.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div role="radiogroup" aria-label={ariaLabel} className="grid grid-cols-2 gap-3">
          {products.map((product) => {
            const card = productCardAvailability(product, availability, configuration);
            return (
              <ProductImageCard
                key={product.id}
                product={product}
                selected={productSelected(product)}
                offered={card.offered}
                unavailableReason={card.unavailableReason}
                caption={captionForProduct?.(product)}
                onSelect={() => onSelect(product)}
              />
            );
          })}
        </div>
      )}
      {footer}
    </section>
  );
}

function ExtrasSection({
  configuration,
  availability,
  colourFamilyId,
  onSelectAccessory,
}: {
  configuration: DoorConfiguration;
  availability: HardwarePlacementAvailability;
  colourFamilyId: HardwareColourFamilyId;
  onSelectAccessory: (accessory: 'knocker' | 'spyhole') => void;
}) {
  const knockerSelected = hasAccessory(configuration, 'knocker');
  const spyholeSelected = hasAccessory(configuration, 'spyhole');
  const letterplateCount = availability.letterplatePositions.length;
  const letterplateCaption =
    letterplateCount === 0
      ? 'Not declared on this style'
      : letterplateCount === 1
        ? 'One position on this style'
        : `${letterplateCount} positions on this style`;
  const showLetterplatePlaceholder = rangeLetterplatesForColour(colourFamilyId).length === 0;
  if (!showLetterplatePlaceholder && !availability.knocker && !availability.spyhole) {
    return null;
  }

  return (
    <section>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Extras
      </p>
      <div className="grid grid-cols-2 gap-3">
        {showLetterplatePlaceholder ? (
          <PlaceholderCatalogCard title="Letterplate" caption={letterplateCaption}>
            <ExtraGlyph kind="letterplate" />
          </PlaceholderCatalogCard>
        ) : null}
        {availability.knocker ? (
          <ChoiceCard
            title="Knocker"
            description="Cannot be combined with a spyhole."
            selected={knockerSelected}
            tickPlacement="top-right"
            onSelect={() => onSelectAccessory('knocker')}
          >
            <div className="aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted">
              <ExtraGlyph kind="knocker" />
            </div>
          </ChoiceCard>
        ) : null}
        {availability.spyhole ? (
          <ChoiceCard
            title="Spyhole"
            description="Cannot be combined with a knocker."
            selected={spyholeSelected}
            tickPlacement="top-right"
            onSelect={() => onSelectAccessory('spyhole')}
          >
            <div className="aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted">
              <ExtraGlyph kind="spyhole" />
            </div>
          </ChoiceCard>
        ) : null}
      </div>
    </section>
  );
}

function FurnitureColourFamilySelector({
  colourFamilyId,
  onSelect,
}: {
  colourFamilyId: HardwareColourFamilyId;
  onSelect: (id: HardwareColourFamilyId) => void;
}) {
  return (
    <section>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Furniture colour
      </p>
      <div
        role="radiogroup"
        aria-label="Furniture colour"
        className="grid grid-cols-4 gap-2"
      >
        {HARDWARE_COLOUR_FAMILIES.map((family) => {
          const selected = colourFamilyId === family.id;
          return (
            <button
              key={family.id}
              type="button"
              role="radio"
              aria-label={family.label}
              aria-checked={selected}
              title={family.label}
              onClick={() => onSelect(family.id)}
              className="door-designer-swatch flex min-w-0 cursor-pointer flex-col items-stretch gap-1.5 border-0 bg-transparent p-0 shadow-none"
            >
              <span
                className={cn(
                  'relative block aspect-[4/3] w-full overflow-hidden rounded-sm',
                  selected
                    ? 'ring-2 ring-[#c8c8c4] ring-offset-2 ring-offset-card'
                    : 'ring-1 ring-border'
                )}
                style={{
                  background: family.swatchBackground ?? family.swatchHex,
                }}
              >
                {selected ? (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-xs">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                ) : null}
              </span>
              <span className="line-clamp-2 text-center text-[11px] font-medium leading-tight text-foreground">
                {family.label}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export const FurnitureStage: React.FC<{
  configuration: DoorConfiguration;
  onChange: (next: DoorConfiguration) => void;
}> = ({ configuration, onChange }) => {
  const style = getExternalSlabStyle(configuration.slab.styleId);
  const artwork = useExternalSlabArtwork(style?.publicPath ?? null);
  const availability = hardwarePlacementAvailability(artwork?.hardwareGuides ?? EMPTY_HARDWARE_GUIDES);
  const colourFamilyId =
    configuration.hardware.colourFamilyId ?? DEFAULT_HARDWARE_COLOUR_FAMILY_ID;
  const selectedHandleId = selectedOperatingHandleId(configuration.hardware);
  const selectedHandle = getHardwareProduct(selectedHandleId);
  const selectedPlate = selectedLetterplate(configuration.hardware);
  const selectedPlateProduct = getHardwareProduct(selectedPlate?.id);
  const selectedLockingId = selectedLockingMechanismId(configuration.hardware);
  const selectedLocking = getHardwareProduct(selectedLockingId);
  const selectedRangeId =
    selectedHandle?.rangeId ?? selectedPlateProduct?.rangeId ?? selectedLocking?.rangeId;
  const [rangeId, setRangeId] = useState<HardwareRangeId | null>(selectedRangeId ?? null);
  const browsingRange = HARDWARE_RANGES.find((range) => range.id === rangeId) ?? null;
  const rangeProducts = browsingRange
    ? hardwareProductsByRangeAndColour(browsingRange.id, colourFamilyId)
    : [];
  const handleProducts = rangeProducts.filter(isOperatingHandle);
  const letterplateProducts = rangeProducts.filter((product) => product.family === 'letterplate');
  const knockerProducts = rangeProducts.filter((product) => product.family === 'knocker');
  const spyholeProducts = rangeProducts.filter((product) => product.family === 'spyhole');
  const alsoAvailableProducts = browsingRange
    ? colourTaggedProductsFromOtherRanges(browsingRange.id, colourFamilyId)
    : [];
  const lockingProducts = lockingProductsForFurniture(
    colourFamilyId,
    browsingRange?.id ?? null
  );
  const lockPositions = escutcheonPlacementIds(availability);
  const escutcheonCycleCaption =
    lockPositions.length > 1
      ? 'Tap to cycle through lock positions on this style.'
      : null;
  const lockingCycleFooter =
    escutcheonCycleCaption && lockingProducts.some((product) => product.role === 'escutcheon') ? (
      <p className="mt-3 text-sm text-muted-foreground">{escutcheonCycleCaption}</p>
    ) : null;

  const selectColourFamily = (nextColourFamilyId: HardwareColourFamilyId) => {
    if (nextColourFamilyId === colourFamilyId) return;
    if (
      browsingRange &&
      hardwareProductsByRangeAndColour(browsingRange.id, nextColourFamilyId).length === 0
    ) {
      setRangeId(null);
    }
    onChange({
      ...configuration,
      hardware: withColourFamily(configuration.hardware, nextColourFamilyId),
    });
  };

  const selectHandle = (product: HardwareProduct) => {
    const placementId = slabPlacementIdForProduct(product);
    if (!placementId) return;
    const alreadySelected = selectedHandleId === product.id;
    onChange({
      ...configuration,
      hardware: withOperatingHandle(
        configuration.hardware,
        'external',
        alreadySelected ? null : { id: product.id, placementId }
      ),
    });
  };

  const selectAccessory = (accessory: 'knocker' | 'spyhole') => {
    const alreadySelected = hasAccessory(configuration, accessory);
    onChange({
      ...configuration,
      hardware: withExclusiveAccessory(
        configuration.hardware,
        'external',
        alreadySelected ? null : accessory
      ),
    });
  };

  const selectLetterplateProduct = (product: HardwareProduct) => {
    onChange({
      ...configuration,
      hardware: withLetterplate(
        configuration.hardware,
        'external',
        nextLetterplateSelection(product, availability.letterplatePositions, selectedPlate)
      ),
    });
  };

  const selectLocking = (product: HardwareProduct) => {
    if (product.role === 'escutcheon') {
      onChange({
        ...configuration,
        hardware: withLockingMechanism(
          configuration.hardware,
          'external',
          nextEscutcheonSelection(
            product,
            escutcheonPlacementIds(availability),
            selectedLockingMechanism(configuration.hardware)
          )
        ),
      });
      return;
    }
    const alreadySelected = selectedLockingMechanismId(configuration.hardware) === product.id;
    const placementId = slabPlacementIdForProduct(product);
    onChange({
      ...configuration,
      hardware: withLockingMechanism(
        configuration.hardware,
        'external',
        alreadySelected
          ? null
          : { id: product.id, ...(placementId ? { placementId } : {}) }
      ),
    });
  };

  const selectCatalogProduct = (product: HardwareProduct) => {
    if (isOperatingHandle(product)) {
      selectHandle(product);
      return;
    }
    if (product.family === 'letterplate') {
      selectLetterplateProduct(product);
      return;
    }
    if (product.family === 'locking') {
      selectLocking(product);
    }
  };

  if (browsingRange) {
    const letterplateFooter =
      letterplateProducts.length > 0 && availability.letterplatePositions.length > 1 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Tap to cycle through letterplate positions on this style.
        </p>
      ) : null;
    const alsoAvailableCycleFooter = (
      <>
        {alsoAvailableProducts.some((product) => product.family === 'letterplate') &&
        availability.letterplatePositions.length > 1 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Tap a letterplate to cycle through positions on this style.
          </p>
        ) : null}
        {escutcheonCycleCaption &&
        alsoAvailableProducts.some((product) => product.role === 'escutcheon') ? (
          <p className="mt-3 text-sm text-muted-foreground">{escutcheonCycleCaption}</p>
        ) : null}
      </>
    );

    return (
      <div className="flex flex-col gap-6">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit gap-1.5"
          aria-label="All ranges"
          onClick={() => setRangeId(null)}
        >
          <ArrowLeft className="h-4 w-4" />
          All ranges
        </Button>
        <h2 className="text-base font-semibold uppercase tracking-wide text-foreground">
          {browsingRange.label}
        </h2>
        <FurnitureColourFamilySelector
          colourFamilyId={colourFamilyId}
          onSelect={selectColourFamily}
        />
        {rangeOwnsFamily(browsingRange.id, isOperatingHandle) ? (
          <CatalogProductSection
            title="Door handle"
            ariaLabel="Door handle"
            products={handleProducts}
            emptyMessage="No handles in this colour yet."
            selectedId={selectedHandleId}
            configuration={configuration}
            availability={availability}
            onSelect={selectCatalogProduct}
          />
        ) : null}
        {rangeOwnsFamily(browsingRange.id, (product) => product.family === 'letterplate') ? (
          <CatalogProductSection
            title="Letterplate"
            ariaLabel="Letterplate"
            products={letterplateProducts}
            emptyMessage="No letterplates in this colour yet."
            selectedId={selectedPlate?.id}
            configuration={configuration}
            availability={availability}
            footer={letterplateFooter}
            onSelect={selectCatalogProduct}
          />
        ) : null}
        {rangeOwnsFamily(browsingRange.id, (product) => product.family === 'knocker') ? (
          <CatalogProductSection
            title="Knockers"
            ariaLabel="Knockers"
            products={knockerProducts}
            emptyMessage="No knockers in this colour yet."
            selectedId={configuration.hardware.external.find((item) =>
              knockerProducts.some((product) => product.id === item.id)
            )?.id}
            configuration={configuration}
            availability={availability}
            onSelect={selectCatalogProduct}
          />
        ) : null}
        {rangeOwnsFamily(browsingRange.id, (product) => product.family === 'spyhole') ? (
          <CatalogProductSection
            title="Spyhole"
            ariaLabel="Spyhole"
            products={spyholeProducts}
            emptyMessage="No spyholes in this colour yet."
            selectedId={configuration.hardware.external.find((item) =>
              spyholeProducts.some((product) => product.id === item.id)
            )?.id}
            configuration={configuration}
            availability={availability}
            onSelect={selectCatalogProduct}
          />
        ) : null}
        <LockingMechanismsSection
          configuration={configuration}
          availability={availability}
          products={lockingProducts}
          footer={lockingCycleFooter}
          onSelect={selectLocking}
        />
        <CatalogProductSection
          title="Also available in this colour"
          ariaLabel="Also available in this colour"
          products={alsoAvailableProducts}
          isSelected={(product) =>
            product.id === selectedHandleId ||
            product.id === selectedPlate?.id ||
            product.id === selectedLockingMechanismId(configuration.hardware)
          }
          configuration={configuration}
          availability={availability}
          captionForProduct={(product) => getHardwareRange(product.rangeId)?.label}
          footer={alsoAvailableCycleFooter}
          onSelect={selectCatalogProduct}
        />
        <p className="text-sm text-muted-foreground">
          A lever and a bar handle cannot be combined. A bar handle needs an escutcheon. A
          finger-pull escutcheon does not use an external handle.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <FurnitureColourFamilySelector
        colourFamilyId={colourFamilyId}
        onSelect={selectColourFamily}
      />
      <section>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Furniture range
        </p>
        <div className="grid grid-cols-2 gap-3">
          {HARDWARE_RANGES.map((range) => {
            const products = hardwareProductsByRangeAndColour(range.id, colourFamilyId);
            return (
              <RangeCard
                key={range.id}
                range={range}
                products={products}
                selected={selectedRangeId === range.id}
                onSelect={() => setRangeId(range.id)}
              />
            );
          })}
        </div>
      </section>
      <LockingMechanismsSection
        configuration={configuration}
        availability={availability}
        products={lockingProducts}
        footer={lockingCycleFooter}
        onSelect={selectLocking}
      />
      <ExtrasSection
        configuration={configuration}
        availability={availability}
        colourFamilyId={colourFamilyId}
        onSelectAccessory={selectAccessory}
      />
      <p className="text-sm text-muted-foreground">
        Pick a colour, then a range to see its furniture. A bar handle needs an escutcheon. A
        finger-pull escutcheon does not use an external handle. Letterplate, knocker, and spyhole
        are offered only when the selected slab declares those placements.
      </p>
    </div>
  );
};
